import { DateTime } from 'luxon';
import { parseImsDate, getDateForIms } from 'js/hooks/useFormatter/helpers';

import { ANOTHER_PERIOD_VALUE, CASINO_GAMING_BALANCE, LAST_24_HOURS_VALUE } from './constants';

export const parseError = (err, tkPrefix) => {
    const {
        data: {
            message: dataMessage,
            code: dataCode,
            errorMessage,
            errorCode
        } = {},
        message,
        reason,
        ID,
        isExportError
    } = err;
    const code = errorCode || dataCode;
    const error = code && `${tkPrefix}error.${code}`
        || errorMessage
        || dataMessage
        || message
        || reason;

    if (isExportError) {
        return error;
    }

    return ID === 32014
        ? { summaryError: error }
        : { historyError: error };
};

// region Periods

export const intoDate = (date = new Date()) => typeof date === 'number' ? new Date(date) : date;
export const startOfDay = (date = new Date()) => intoDate(date).setHours(0, 0, 0, 0);

/*
 will return the day three months before the argument day (or current day)
 shifts result`s local hours to preserve hours bonded to 0-time-zone
 Examples:
 with May 31 2021 12:00:00 GMT+0300 get3MonthsBefore() will return new Date('Mar 01 2021 11:00:00 GMT+0200')
 with Jun 01 2021 12:00:00 GMT+0300 get3MonthsBefore() will return new Date('Mar 01 2021 11:00:00 GMT+0200')
 with Dec 27 2020 12:00:00 GMT+0200 get3MonthsBefore() will return new Date('Sep 27 2020 13:00:00 GMT+0300')
 Note: use get3MonthsBefore(get3MonthsBefore()) instead of get6MonthsBefore() because time ranges
 [now; get3MonthsBefore()] + [get3MonthsBefore(); get6MonthsBefore()] in case (now = May 31 2021) appears
 [May 31; Mar 01] + [Mar 01; Dec31], witch is not valid since [Dec31 - Mar 01] - is more than 3 month

@param [now] {Date}
@returns Date
 */
export const get3MonthsBefore = (now = new Date()) => {
    const hour = now.getUTCHours();
    const resultDay = new Date(now.getTime());

    resultDay.setDate(1);
    resultDay.setMonth(now.getMonth() - 3);

    const daysInMonth = new Date(resultDay.getFullYear(), resultDay.getMonth() + 1, 0).getDate();

    if (daysInMonth < now.getDate()) {
        resultDay.setMonth(resultDay.getMonth() + 1);
    } else {
        resultDay.setDate(now.getDate());
    }

    resultDay.setUTCHours(hour);

    return resultDay;
};

export const formatDate = time => {
    const timeRaw = time ? new Date(time) : new Date();

    timeRaw.setMilliseconds(0);

    return getDateForIms(timeRaw);
};

export const getRequestAnotherPeriodDates = (from, until) => {
    const fromDate = new Date(from);
    const untilDate = new Date(until);

    return {
        requestFrom: new Date(Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), 0, 0, 0, 0)),
        requestUntil: new Date(Date.UTC(untilDate.getFullYear(), untilDate.getMonth(), untilDate.getDate(), 23, 59, 59, 0))
    };
};

export const getSecondBulkForSixMonthsDates = () => ({
    startDate: get3MonthsBefore(get3MonthsBefore()),
    endDate: get3MonthsBefore()
});

export const getRequestDates = ({ period, startDate, endDate }) => {
    const isAnotherPeriod = period === ANOTHER_PERIOD_VALUE;
    const { requestFrom, requestUntil } = getRequestAnotherPeriodDates(startDate, endDate);

    return {
        requestStartDate: isAnotherPeriod ? requestFrom : startDate,
        requestEndDate: isAnotherPeriod ? requestUntil : endDate
    };
};

export const getInitialPeriodData = () => {
    const currentDate = DateTime.local();
    const days = LAST_24_HOURS_VALUE;

    return {
        period: days,
        startDate: currentDate.minus({ days }).toJSDate(),
        endDate: currentDate.toJSDate()
    };
};

// endregion

// region Summary
export const mergeSummary = (summary1 = {}, summary2) => {
    if (!summary2) {
        return summary1;
    }

    return Object.keys(summary1).reduce((acc, category) => {
        const summaryValue1 = summary1[category];
        const summaryValue2 = summary2[category];

        if ('amount' in summaryValue1) {
            return {
                ...acc,
                [category]: {
                    amount: Number(summaryValue1.amount) + Number(summaryValue2.amount),
                    currencyCode: summaryValue1.currencyCode
                }
            };
        }

        return {
            ...acc,
            [category]: Object.keys(summaryValue1).reduce((total, balance) => ({
                ...total,
                [balance]: {
                    amount: Number(summaryValue1[balance].amount) + Number(summaryValue2[balance].amount),
                    currencyCode: summaryValue1[balance].currencyCode
                }
            }), {})
        };
    }, {});
};

export const getCasinoBalance = (balances) => balances?.find(item => item.balanceType === CASINO_GAMING_BALANCE)?.balance || {};

export const getStartingBalance = (balances1, balances2) => {
    const { currencyCode, amount } = getCasinoBalance(balances2 || balances1);

    return { amount, currencyCode };
};

// endregion

// region Transactions

export const parseHistoryData = (data) => {
    const { transactions, pagingResponseParams } = data;
    const { nextPageId = null } = pagingResponseParams;

    return {
        page: {
            nextPageId,
            isPageChanged: false
        },
        transactions: parseTransactions(transactions)
    };
};

export const parseTransactions = (data) => data.map(transaction => {
    const {
        time,
        methodCode,
        paymentAccountDetails: { additionalPaymentsFields = [], ...restPaymentAccountDetails }
    } = transaction;
    const paymentAccountDetails = methodCode === 'DotpayCard'
        ? {
            ...restPaymentAccountDetails,
            additionalPaymentsFields: additionalPaymentsFields.map(item => ({
                ...item,
                value: item.key.toLocaleLowerCase() === 'accountid' ? `*${item.value.substr(-4)}` : item.value
            }))
        }
        : transaction.paymentAccountDetails;

    return {
        ...transaction,
        paymentAccountDetails,
        time: Number(parseImsDate(time))
    };
});

// endregion
